import { isEmpty } from 'lodash';

import { ia, iaRa } from 'lib/helpers/utility';

export const isUnsavedEmptyRow = (row) =>
  !!row?.unsaved && !String(row?.merged_sentences ?? '').trim();

export const withoutUnsavedEmptyRows = (transcripts) =>
  iaRa(transcripts).map((t) =>
    t?.transcript_enhanced
      ? { ...t, transcript_enhanced: t.transcript_enhanced.filter((r) => !isUnsavedEmptyRow(r)) }
      : t
  );

// Speaker indices are stored 0-based (diarization), but shown 1-based to users.
// Handles raw numbers and pre-formatted "Speaker N" strings; leaves custom names
// (e.g. "Provider") untouched.
export const formatSpeakerLabel = (value) => {
  if (typeof value === 'number') return `Speaker ${value + 1}`;
  const match = /^Speaker\s+(\d+)$/.exec(String(value ?? ''));
  if (match) return `Speaker ${Number(match[1]) + 1}`;
  return value;
};

export const formatSeconds = ({ seconds }) => {
  if (!seconds) return '00:00';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const replaceSpeakerWithBrackets = ({ text = null }) => {
  if (!text) return text;

  // Matches instances of "Speaker" followed by numbers (\d+) and optional colon, within brackets or not.
  const regex = /(\[Speaker (\d+):?\])|Speaker (\d+)(:?)(:?)(:?) ?/g;

  return text.replace(regex, (match, brackets, num1, num2) => {
    const number = num1 || num2;

    return `[Speaker ${number}] `;
  });
};

export const mergeTexts = ({ currentText = null, newText = null }) => {
  const mergedText = [currentText ?? '', newText ?? ''].join(' ');

  return replaceSpeakerWithBrackets({ text: mergedText });
};

export const replaceSpeakers = ({ text = null, speakers = [] }) => {
  if (!text) return text;
  if (!ia(speakers)) return text;

  const regex = /\[([^[\]]+)\]/g; // Dynamic regex to match anything inside brackets

  return text.replace(regex, (match, label) => {
    const speaker = speakers.find((s) => s.old_name === label);
    if (speaker) {
      return `[${speaker.new_name}]`;
    } else {
      return match; // Keep unchanged if not found in speakers
    }
  });
};

export const processTranscriptData = ({
  prevState = [],
  currentTranscriptDataId = null,
  currentSpeakerId = null,
  newName = null,
  initialName = null
}) => {
  const targetIndex = prevState.findIndex(
    (data) => (data?.id || data?.temp_id) === currentTranscriptDataId
  );

  if (targetIndex === -1) {
    return prevState;
  }

  const targetData = prevState[targetIndex];

  const updatedSentences = targetData.transcript_enhanced.map((obj) => {
    if (obj.speaker_id === currentSpeakerId) {
      if (isEmpty(newName)) {
        return { ...obj, speaker: obj?.new_name || initialName };
      }
      return { ...obj, speaker: newName };
    }
    return obj;
  });

  const updatedTranscriptData = {
    ...targetData,
    transcript_enhanced: updatedSentences
  };

  const updatedSpeakers = targetData.speakers.map((obj) => {
    if (obj.id === currentSpeakerId) {
      return { ...obj, new_name: newName, old_name: obj.new_name };
    }
    return obj;
  });

  let updatedSummary = targetData.summary;
  if (updatedSummary) {
    updatedSummary = replaceSpeakers({
      speakers: updatedSpeakers,
      text: updatedSummary
    });
  }

  const newState = [...prevState];
  newState[targetIndex] = {
    ...updatedTranscriptData,
    speakers: updatedSpeakers,
    summary: updatedSummary
  };

  return newState;
};
